import React, { useState } from 'react';
import { Box, Paper, Typography, Button, CircularProgress, useTheme, Chip, alpha } from '@mui/material';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, lastDayOfMonth, getDate } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useQuery } from '@tanstack/react-query';
import { tarefaService, Tarefa } from '../services/tarefaService';
import AddTarefaModal from '../components/modals/AddTarefaModal';
import ExecutarTarefaModal from '../components/modals/ExecutarTarefaModal';
import { Add as AddIcon } from '@mui/icons-material';
import { getBigCalendarStyles } from '../styles/theme';

const locales = {
  'pt-BR': ptBR,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: Tarefa;
}

const TarefasPage: React.FC = () => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExecutarModalOpen, setIsExecutarModalOpen] = useState(false);
  const [selectedTarefa, setSelectedTarefa] = useState<Tarefa | undefined>(undefined);
  const [selectedEventDate, setSelectedEventDate] = useState<Date | undefined>(undefined);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<any>(Views.MONTH);

  const { data: tarefasResponse, isLoading, refetch } = useQuery({
    queryKey: ['tarefas'],
    queryFn: tarefaService.getTarefas
  });

  const tarefas = tarefasResponse?.data || [];

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedTarefa(event.resource);
    setSelectedEventDate(event.start);
    setIsModalOpen(true);
  };

  const handleAddTarefa = () => {
    setSelectedTarefa(undefined);
    setSelectedEventDate(undefined);
    setIsModalOpen(true);
  };

  const handleOpenExecutar = (tarefa: Tarefa, referenceDate?: Date) => {
    setSelectedTarefa(tarefa);
    setSelectedEventDate(referenceDate);
    setIsExecutarModalOpen(true);
  };

  const events: CalendarEvent[] = [];
  if (tarefas.length > 0) {
    const currentYear = currentDate.getFullYear();

    tarefas.forEach((t: Tarefa) => {
      let displayTitle = t.titulo;
      if (t.setor) displayTitle = `[${t.setor}] ${displayTitle}`;

      const applyTime = (baseDate: Date) => {
        if (!t.hora_tarefa) return baseDate;
        const [hours, minutes] = t.hora_tarefa.split(':').map(Number);
        const newDate = new Date(baseDate);
        newDate.setHours(hours || 0, minutes || 0, 0, 0);
        return newDate;
      };

      if (t.tipo_agendamento === 'data' && t.data_agendada) {
        const dateObjStart = new Date(t.data_agendada);
        dateObjStart.setMinutes(dateObjStart.getMinutes() + dateObjStart.getTimezoneOffset());

        let start = new Date(dateObjStart);
        let end = new Date(dateObjStart);

        if (t.hora_tarefa) {
          start = applyTime(start);
          end = new Date(start);
          end.setHours(end.getHours() + 1);
        } else {
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
        }

        events.push({
          id: t.id,
          title: displayTitle,
          start,
          end,
          resource: t,
          allDay: !t.hora_tarefa
        });
      } else if (t.tipo_agendamento === 'recorrente' && t.dia_recorrente) {
        const diaR = Number(t.dia_recorrente);
        for (let year = currentYear - 1; year <= currentYear + 1; year++) {
          for (let month = 0; month < 12; month++) {
            const baseDate = new Date(year, month, diaR);
            if (baseDate.getMonth() === month) {
              let start = new Date(baseDate);
              let end = new Date(baseDate);

              if (t.hora_tarefa) {
                start = applyTime(start);
                end = new Date(start);
                end.setHours(end.getHours() + 1);
              } else {
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
              }

              events.push({
                id: Number(`${t.id}${year}${month}`),
                title: `${displayTitle} (Recorrente)`,
                start,
                end,
                resource: t,
                allDay: !t.hora_tarefa
              });
            }
          }
        }
      }
    });
  }

  const getEventColor = (event: CalendarEvent) => {
    if (event.resource.tipo_agendamento === 'data') {
      if (event.resource.status === 'executada') return theme.palette.success.main;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (event.start < today && event.resource.status === 'pendente') return theme.palette.error.main;
      return theme.palette.warning.main;
    } else {
      // Para tarefas recorrentes: verificar usando mes_referencia (YYYY-MM)
      let executadaNoMes = false;
      const execucoes = event.resource.execucoes || [];
      const mesReferenciaEvento = format(event.start, 'yyyy-MM');
      
      if (execucoes.length > 0) {
        executadaNoMes = execucoes.some((exec: any) => {
          return exec.mes_referencia === mesReferenciaEvento;
        });
      }
      
      if (executadaNoMes) return theme.palette.success.main;
      const today = new Date();
      if (event.start < today) return theme.palette.error.main;
      return theme.palette.info.main;
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const backgroundColor = getEventColor(event);

    if (currentView === Views.AGENDA) {
      return {
        style: {
          borderLeft: `5px solid ${backgroundColor}`,
          backgroundColor: alpha(backgroundColor, 0.05),
          color: theme.palette.text.primary
        }
      };
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '0.85rem',
        padding: '2px 5px'
      }
    };
  };

  // Função utilitária para formatar label da data na agenda
  const formatAgendaDateLabel = (date: Date, label: string): { text: string; color: string } => {
    try {
      if (!date || isNaN(date.getTime())) {
        return { text: label || '---', color: theme.palette.text.secondary };
      }

      const dateStr = format(date, 'yyyy-MM-dd');
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const yesterdayStr = format(new Date(new Date().setDate(new Date().getDate() - 1)), 'yyyy-MM-dd');
      const tomorrowStr = format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd');

      if (dateStr === todayStr) return { text: 'Hoje', color: theme.palette.primary.main };
      if (dateStr === yesterdayStr) return { text: 'Ontem', color: theme.palette.error.main };
      if (dateStr === tomorrowStr) return { text: 'Amanhã', color: theme.palette.success.main };

      return { text: format(date, "EEE, dd 'de' MMM", { locale: ptBR }), color: theme.palette.text.secondary };
    } catch (e) {
      return { text: label || '---', color: theme.palette.text.secondary };
    }
  };

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 },
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
          Agenda de Tarefas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTarefa}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
        >
          Nova Tarefa
        </Button>
      </Box>

      <Paper elevation={0} sx={{
        p: 2,
        flexGrow: 1,
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        ...(theme.palette.mode === 'dark' && {
          backgroundImage: 'none',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.28)',
        }),
      }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            date={currentDate}
            onNavigate={(date) => setCurrentDate(date)}
            view={currentView}
            onView={(view) => {
              setCurrentView(view);
              if (view === Views.AGENDA) {
                const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                setCurrentDate(startOfMonth);
              }
            }}
            length={getDate(lastDayOfMonth(currentDate)) - 1}
            style={{ height: '100%', width: '100%' }}
            messages={{
              next: "Próximo",
              previous: "Anterior",
              today: "Hoje",
              month: "Mês",
              week: "Semana",
              day: "Dia",
              agenda: "Agenda",
              date: "Data",
              time: "Hora",
              event: "Evento",
              allDay: "Dia todo",
              noEventsInRange: "Não há tarefas neste período.",
              showMore: (total) => `+${total} mais`
            }}
            culture='pt-BR'
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            popup
            selectable
            components={{
              agenda: {
                date: ({ label, day }: any) => {
                  const { text, color } = formatAgendaDateLabel(day, label);
                  return (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color,
                        whiteSpace: 'nowrap',
                        fontSize: '0.82rem'
                      }}
                    >
                      {text}
                    </Typography>
                  );
                },
                event: ({ event }) => {
                  const color = getEventColor(event as any);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: color
                      }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(event as any).title}
                      </Typography>
                    </Box>
                  );
                }
              }
            }}
          />
        )}
      </Paper>

      <AddTarefaModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={refetch}
        tarefa={selectedTarefa}
        onExecutar={handleOpenExecutar}
        instanceDate={selectedEventDate}
      />

      <ExecutarTarefaModal
        open={isExecutarModalOpen}
        onClose={() => {
          setIsExecutarModalOpen(false);
          setSelectedEventDate(undefined);
        }}
        onSuccess={refetch}
        tarefa={selectedTarefa}
        referenceDate={selectedEventDate}
      />

      <style>{getBigCalendarStyles(theme)}</style>
    </Box>
  );
};

export default TarefasPage;
